// src/components/roles/forms/UpdateRole.tsx
import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiType } from "react-icons/fi";

export type EditRolePayload = {
  name: string;
  isSystemRole: boolean;
};

export type EditRoleProps = {
  id: number | string;
  name: string;
  isSystemRole: boolean;
};

export default function UpdateRole({
  role,
  onSubmit,
  onCancel,
}: {
  role: EditRoleProps;
  onSubmit: (payload: EditRolePayload) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<EditRolePayload>({
    name: role.name,
    isSystemRole: role.isSystemRole,
  });

  // Keep form in sync if parent swaps the role while modal is open
  useEffect(() => {
    setForm({ name: role.name, isSystemRole: role.isSystemRole });
  }, [role]);

  const change = <K extends keyof EditRolePayload>(k: K, v: EditRolePayload[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({ name: form.name.trim(), isSystemRole: form.isSystemRole });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4">
      {/* Role name */}
      <Field label="Role name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., Admin"
          required
        />
      </Field>

      {/* System user role toggle */}
      <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          checked={form.isSystemRole}
          onChange={(e) => change("isSystemRole", e.target.checked)}
        />
        <span>
          <span className="font-medium text-gray-900">System user role</span>
          <span className="mt-0.5 block text-xs text-gray-500">
            Make this role assignable when creating or editing users. Leave
            unchecked for roles managed from their own section (e.g. Customer,
            Affiliate Partner, Supplier).
          </span>
        </span>
      </label>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
}
